import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';
import DashboardCalendar from './DashboardCalendar';

const { FiUsers, FiBriefcase, FiCheckSquare, FiAlertCircle, FiCheck, FiArrowRight } = FiIcons;

const Dashboard = () => {
  const { contacts, projects, tasks, updateTask } = useData();
  
  const todoTasks = tasks.filter(t => t.status === 'To Do').slice(0, 5);
  
  const stats = [
    { title: 'Contacts', value: contacts.length, icon: FiUsers, color: 'bg-blue-600', shadow: 'shadow-blue-100' },
    { title: 'Projects', value: projects.length, icon: FiBriefcase, color: 'bg-indigo-600', shadow: 'shadow-indigo-100' },
    { title: 'To Do', value: tasks.filter(t => t.status === 'To Do').length, icon: FiCheckSquare, color: 'bg-slate-900', shadow: 'shadow-slate-200' }
  ];

  const handleToggleComplete = (task) => {
    updateTask(task.id, { status: 'Completed' });
  };

  return (
    <div className="space-y-10 pb-20">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tighter">Workspace Overview</h1>
          <p className="text-slate-400 font-medium">Real-time business health and schedule audit.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {stats.map(stat => (
          <div key={stat.title} className="bg-white p-8 rounded-[3rem] shadow-sm border border-slate-100 flex items-center justify-between group hover:shadow-xl transition-all cursor-default">
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{stat.title}</p>
              <p className="text-4xl font-black text-slate-900 mt-1">{stat.value}</p>
            </div>
            <div className={`${stat.color} w-16 h-16 rounded-[2rem] flex items-center justify-center text-white shadow-2xl ${stat.shadow} group-hover:scale-110 transition-transform`}>
              <SafeIcon icon={stat.icon} className="text-2xl" />
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <DashboardCalendar tasks={tasks} projects={projects} />
        </div>
        
        <div className="space-y-10">
          <div className="bg-white rounded-[3rem] shadow-sm border border-slate-100 p-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl font-black text-slate-900 flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center text-orange-500 text-sm">
                   <SafeIcon icon={FiAlertCircle} />
                </div>
                Priority Queue
              </h2>
              <button className="text-[10px] font-black text-blue-600 uppercase tracking-widest hover:underline flex items-center gap-1">
                View All <SafeIcon icon={FiArrowRight} />
              </button>
            </div>
            
            <div className="space-y-4">
              {todoTasks.map(task => (
                <div key={task.id} className="flex items-center justify-between p-5 bg-slate-50/50 rounded-[2rem] hover:bg-slate-50 transition-all border border-transparent hover:border-slate-100 group">
                  <div className="flex items-center gap-4">
                    <button 
                      onClick={() => handleToggleComplete(task)}
                      className="w-10 h-10 bg-white rounded-xl flex items-center justify-center border border-slate-200 text-slate-300 hover:text-green-500 hover:border-green-500 transition-all shadow-sm"
                    >
                      <SafeIcon icon={FiCheck} />
                    </button>
                    <div>
                      <p className="font-black text-slate-900 text-sm">{task.title}</p>
                      <p className="text-[10px] text-slate-400 uppercase tracking-tighter font-black">{task.project_name || task.project}</p>
                    </div>
                  </div>
                  <span className={`text-[8px] font-black px-2 py-1 rounded-lg uppercase ${task.priority === 'High' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
                    {task.priority}
                  </span>
                </div>
              ))}
              {todoTasks.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-200">
                    <SafeIcon icon={FiCheck} className="text-2xl" />
                  </div>
                  <p className="text-slate-400 font-bold">Clear Schedule</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;